import React from 'react';
import CreatableSelect from 'react-select/creatable';
import { components } from 'react-select';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';

function arrayMove(array, from, to) {
  const slicedArray = array.slice();
  slicedArray.splice(
    to < 0 ? array.length + to : to,
    0,
    slicedArray.splice(from, 1)[0]
  );
  return slicedArray;
}

const SortableMultiValue = SortableElement(
  (props) => {
    const onMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const innerProps = { ...props.innerProps, onMouseDown };
    return <components.MultiValue {...props} innerProps={innerProps} />;
  }
);

const SortableMultiValueLabel = SortableHandle(
  (props) => <components.MultiValueLabel {...props} />
);

const SortableCreatableSelect = SortableContainer(CreatableSelect);

const createOption = (label) => ({
  label,
  value: label,
});

const CreatableSortableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Type something and press enter...",
  hideDropdownIndicator = true,
  closeMenuOnCreate = true,
  isValidNewOption,
  formatCreateLabel,
  onCreateOption,
  onInputChange: customOnInputChange,
  splitDelimiters,
  maxValues,
  ...restProps
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [menuIsOpen, setMenuIsOpen] = React.useState(undefined);

  const handleKeyDown = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        // Check if the option is valid before creating
        if (isValidNewOption && !isValidNewOption(inputValue, value, value || [])) {
          return;
        }

        // Use custom create function if provided, otherwise default
        const newOption = onCreateOption
          ? onCreateOption(inputValue)
          : createOption(inputValue);

        if (newOption) {
          onChange([...(value || []), newOption]);
          setInputValue('');

          // Close menu after creating if closeMenuOnCreate is true
          if (closeMenuOnCreate) {
            setMenuIsOpen(false);
          }

          event.preventDefault();
        }
    }
  };

  const handleInputChange = (newValue, actionMeta) => {
    // Only react to the user actually typing. react-select also fires this
    // with newValue='' on blur / menu-close / set-value, which would wipe
    // in-progress text the user expects to still be there.
    if (actionMeta.action !== 'input-change') return;

    if (customOnInputChange) {
      const processedValue = customOnInputChange(newValue, actionMeta);
      setInputValue(processedValue);
    } else {
      setInputValue(newValue);
    }

    if (newValue) {
      setMenuIsOpen(undefined); // Let react-select handle it naturally
    }
  };

  const handleChange = (newValue, actionMeta) => {
    onChange(newValue || []);

    // Close menu after selecting/creating if closeMenuOnCreate is true
    if (closeMenuOnCreate && actionMeta.action === 'create-option') {
      setMenuIsOpen(false);
      setInputValue('');
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newValue = arrayMove(value || [], oldIndex, newIndex);
    onChange(newValue);
  };

  const handlePaste = (event) => {
    if (!splitDelimiters) return;
    const pasted = event.clipboardData.getData('text');
    if (!pasted) return;

    // Newlines always win: if the user pasted multiple lines, treat each line
    // as one value rather than further splitting on commas/tabs (which often
    // appear inside a single value, e.g. "Smith, John"). Trim first so a
    // trailing newline on an otherwise comma-separated list doesn't flip us
    // into multi-line mode.
    const trimmed = pasted.trim();
    const hasNewlines = /\r?\n/.test(trimmed);
    if (!hasNewlines && !splitDelimiters.test(trimmed)) return;

    event.preventDefault();

    const splitter = hasNewlines ? /\r?\n+/ : splitDelimiters;
    const existing = value || [];
    const existingValues = new Set(existing.map((o) => o.value));
    const tokens = pasted
      .split(splitter)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const newOptions = [];
    tokens.forEach((token) => {
      if (existingValues.has(token)) return;
      existingValues.add(token);
      newOptions.push(onCreateOption ? onCreateOption(token) : createOption(token));
    });

    if (newOptions.length === 0) return;

    let combined = [...existing, ...newOptions];
    if (typeof maxValues === 'number' && combined.length > maxValues) {
      combined = combined.slice(0, maxValues);
    }
    onChange(combined);
    setInputValue('');
  };

  // Keep the custom Input component identity stable across renders. If we
  // rebuild it whenever value/onChange change, react-select unmounts and
  // remounts the underlying <input>, which loses focus mid-typing — which
  // would happen on every Enter (value changes) or any parent re-render
  // (onChange is a fresh closure).
  const handlePasteRef = React.useRef(handlePaste);
  handlePasteRef.current = handlePaste;

  const PasteCapturingInput = React.useMemo(() => {
    if (!splitDelimiters) return undefined;
    return (inputProps) => (
      <components.Input {...inputProps} onPaste={(e) => handlePasteRef.current(e)} />
    );
  }, [splitDelimiters]);

  const customComponents = {
    ...(hideDropdownIndicator ? { DropdownIndicator: null } : {}),
    MultiValue: SortableMultiValue,
    MultiValueLabel: SortableMultiValueLabel,
    ...(PasteCapturingInput ? { Input: PasteCapturingInput } : {}),
    ...restProps.components,
  };


  return (
    <SortableCreatableSelect
      useDragHandle
      // react-sortable-hoc props:
      axis="xy"
      onSortEnd={onSortEnd}
      distance={4}
      getHelperDimensions={({ node }) => node.getBoundingClientRect()}
      // react-select props:
      components={customComponents}
      inputValue={inputValue}
      isClearable
      isMulti
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      value={value}
      options={options}
      isValidNewOption={isValidNewOption}
      formatCreateLabel={formatCreateLabel}
      noOptionsMessage={() => null}
      {...restProps}
    />
  );
};

export default CreatableSortableSelect;
